// apps/web/src/pages/spread/settings/VocabularyTab.tsx
//
// Phase 9 Plan 09-12 Task 2 — Vocabulary tab of the SpreadSettingsDialog.
//
// Renders one row per VocabularySlot (container/subdivision/set/item) with:
//   - Live-preview chip showing the current effective term via useTerm(slot).
//     useTerm reads from <SpreadVocabularyProvider> (Plan 09-04 Task 2) which
//     itself is fed by SpreadComposer's VocabularyProviderWrapper — useWatch
//     on `vocabularyOverrides` propagates form-state edits to the provider.
//   - FormField <Input> bound to `vocabularyOverrides.<slot>` with maxLength
//     === VOCABULARY_SLOT_MAX_CHARS (50). Zod backstops at the route.
//   - Reset button per slot — clears the override (sets value to undefined).
//
// UI-SPEC §"Vocabulary tab" (lines 432-442). The header copy itself uses
// `useTerm('container')` so "Rename what these slots are called in this
// {container.lower}" reflects renames the author has already applied.
//
// SECURITY:
//   - Override values are user-supplied strings. They are rendered as JSX
//     text content (no dangerouslySetInnerHTML). Plan 13's grep gate
//     forbids dangerouslySetInnerHTML wrapping useTerm results.
//   - The maxLength HTML attribute is a UX gate (prevents accidental paste
//     of 10 KB strings). Zod at the API layer enforces the bound at the
//     security boundary (apps/api/src/routes/spreads-write.ts).
//
// FORM STATE PATH:
//   `vocabularyOverrides.<slot>` — top-level field on the spread form.
//   SpreadComposer's VocabularyProviderWrapper subscribes to this path and
//   feeds the provider. Edit -> setValue -> useWatch fires -> provider
//   re-renders -> useTerm consumers update.

import { RotateCcw } from "lucide-react";
import { useFormContext } from "react-hook-form";
import {
  VOCABULARY_DEFAULTS,
  VOCABULARY_SLOT_MAX_CHARS,
  VOCABULARY_SLOTS,
  type VocabularySlot,
} from "@the-recipe-spread/shared";
import { Button } from "@/components/ui/button";
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { useTerm } from "@/lib/vocabulary/useTerm";

// Per-slot copy from UI-SPEC §"Vocabulary tab" lines 437-440.
const SLOT_LABEL: Record<VocabularySlot, string> = {
  container: "Container term",
  subdivision: "Subdivision term",
  set: "Set term",
  item: "Item term",
};
const SLOT_HELPER: Record<VocabularySlot, string> = {
  container: `Default: "${VOCABULARY_DEFAULTS.container}"`,
  subdivision: `Default: "${VOCABULARY_DEFAULTS.subdivision}"`,
  set: `Default: varies by recipe (e.g. "Flavor Set").`,
  item: `Default: varies by recipe (e.g. "Flavor").`,
};

export function VocabularyTab(): React.ReactElement {
  const { control } = useFormContext();
  const container = useTerm("container");

  return (
    <div className="flex flex-col gap-6">
      <header className="flex flex-col gap-1">
        <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          VOCABULARY
        </h3>
        <p className="text-sm text-muted-foreground">
          Rename what these slots are called in this {container.toLowerCase()}. Changes apply
          everywhere the term appears.
        </p>
      </header>
      <div className="flex flex-col gap-5">
        {VOCABULARY_SLOTS.map((slot) => (
          <VocabularySlotRow key={slot} slot={slot} />
        ))}
      </div>
    </div>
  );
}
VocabularyTab.displayName = "VocabularyTab";

interface VocabularySlotRowProps {
  slot: VocabularySlot;
}

function VocabularySlotRow({ slot }: VocabularySlotRowProps): React.ReactElement {
  const { control, setValue } = useFormContext();
  // useTerm subscribes via context — re-renders when the provider value
  // changes (driven by SpreadComposer's useWatch on vocabularyOverrides).
  const liveValue = useTerm(slot);

  return (
    <FormField
      control={control}
      name={`vocabularyOverrides.${slot}`}
      render={({ field }) => (
        <FormItem data-field-path={`vocabularyOverrides.${slot}`}>
          <FormLabel>{SLOT_LABEL[slot]}</FormLabel>
          {/* Live-preview chip — UI-SPEC line 441. Always present so the
              author can see the effective term as they type. */}
          <div className="text-xs text-muted-foreground" data-live-preview={slot}>
            Currently: <span className="font-medium text-foreground">{liveValue}</span>
          </div>
          <div className="flex items-center gap-2">
            <FormControl>
              <Input
                {...field}
                value={typeof field.value === "string" ? field.value : ""}
                onChange={(e) => {
                  const next = e.target.value;
                  // Empty string -> store undefined so the override is
                  // semantically removed (provider falls back to default).
                  field.onChange(next.length === 0 ? undefined : next);
                }}
                placeholder={VOCABULARY_DEFAULTS[slot]}
                maxLength={VOCABULARY_SLOT_MAX_CHARS}
                aria-label={SLOT_LABEL[slot]}
              />
            </FormControl>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              aria-label={`Reset ${SLOT_LABEL[slot]} to default`}
              onClick={() => setValue(`vocabularyOverrides.${slot}`, undefined, { shouldDirty: true })}
            >
              <RotateCcw size={12} aria-hidden />
              <span>Reset</span>
            </Button>
          </div>
          <FormDescription>{SLOT_HELPER[slot]}</FormDescription>
          <FormMessage />
        </FormItem>
      )}
    />
  );
}
